import {
  BALL_RADIUS,
  BRANCH_WIDTH,
  Hazard,
  HazardKind,
  MOOSE_RADIUS,
  PAD_LENGTH,
  PAD_WIDTH,
  POOL_RADIUS,
  POST_RADIUS,
  RAMP_HEIGHT,
  RAMP_LENGTH,
  RAMP_WIDTH,
  SLUSH_LENGTH,
  SLUSH_WIDTH,
  SWARM_RADIUS,
  TIMBER_LENGTH,
  TIMBER_WIDTH,
  Track,
  halfAt,
  hazardLat,
  inWater,
  locate,
  loopDelta,
  nearestCut,
  narrowest,
  onIce,
  roadY,
} from "./tracks";
import { TUNING } from "./tuning";

// The race as plain data and one fixed-step function over it. No rendering,
// no DOM: the component feeds it the player's steering, the renderer reads it
// back, and the test drives it headless.

// The items are the lodge's own, not the plumber's:
//   energy    an energy drink, bolt on the can — a burst of speed
//   beer      spilt on the road behind you; whoever drives through it twirls
//   glove     a boxing glove fired straight ahead, off the walls; a hit
//             twirls you and knocks you sideways
//   tuborg    a six-pack of Grøn Tuborg: six cans fired straight ahead in a
//             quick row, off the walls; they don't fly as far as the glove,
//             and a hit twirls you
//   mosquito  a swarm that hunts down the kart ahead; being bitten slows you
//             and sets you wobbling for a couple of seconds
//   smoke     a smoke machine on the back of your kart: for a few seconds
//             it blows a cloud around you, and anyone else in it is slowed
//   soundboks a Soundboks on the back of your kart playing hard bass: every
//             beat throws the karts round you off the road's line — up in
//             the air, shoved away and robbed of some speed
//   barf      you lean out over the side and throw up; the puddle behind
//             you slows whoever drives through it to a crawl
//   bearnaise a tub of chili bearnaise tipped out on the road: drive
//             through it and you slide off, sideways, into the barrier
//   jager     a Jägermeister — a burst of speed, as the energy drink
//   cart      your kart becomes a shopping trolley: faster, and nothing
//             stops it — it bowls over anyone it touches. The further
//             back you are, the likelier it is; never for the leader
//   rocket    a New Year's rocket, for karts well behind: it flies up the road
//             after whoever's leading and bursts over them, twirling everyone
//             near. A Soundboks's beat knocks it out of the air
//   moose     rare, and only for whoever's last and well behind: you become
//             a moose, far faster than any kart, that nothing can stop and
//             that bowls over anyone in its way — until you close in on
//             first place
//
// What each is worth — how long, how fast, how likely — is in tuning.ts.
export type ItemKind =
  | "energy" | "jager" | "beer" | "barf" | "bearnaise" | "glove" | "tuborg" | "mosquito" | "smoke" | "soundboks" | "cart"
  | "moose" | "rocket";

export const ITEMS: Record<ItemKind, { label: string }> = {
  energy: { label: "Energidrik" },
  beer: { label: "Øl" },
  glove: { label: "Boksehandske" },
  tuborg: { label: "Grøn Tuborg" },
  mosquito: { label: "Myggesværm" },
  smoke: { label: "Røgmaskine" },
  soundboks: { label: "Soundboks" },
  barf: { label: "Bræk" },
  bearnaise: { label: "Chili-bearnaise" },
  jager: { label: "Jägermeister" },
  cart: { label: "Indkøbsvogn" },
  moose: { label: "Elg" },
  rocket: { label: "Nytårsraket" },
};

export type Difficulty = "easy" | "medium" | "hard";

/** The difficulties, by name; how each drives is in tuning.ts. */
export const DIFFICULTIES: Record<Difficulty, string> = { easy: "Let", medium: "Mellem", hard: "Svær" };

export const STEP = 1 / 60;
export const COUNTDOWN = 3;
export const MAX_SPEED = 24;
export const KART_RADIUS = 0.9;
/** Karts bump as two circles, front and back, not one — they're long. */
const BUMP_RADIUS = 0.78;
const BUMP_OFFSET = 0.62;
/** How bouncy a bump is, 0 (dead) to 1 (billiard balls). */
const BUMP_RESTITUTION = 0.35;
/** Sideways knock from a bump dies away at this rate, per second. */
const KNOCK_DECAY = Math.exp(-5 * STEP);
const TURN = 2.6;
const ACCEL = 13;
/** How long a mosquito bite slows you and sets you wobbling. */
const ITCH_TIME = 2.5;
/** How hard a boxing glove knocks you sideways. */
const PUNCH = 12;
/** A six-pack: how many cans, how far apart in time they're fired, and how long each flies (a glove: 4 s). */
const CANS = 6;
const CAN_GAP = 0.12;
const CAN_TIME = 1.2;
/** Top speed wading through a flooded stretch, as a share of the usual. */
const WATER_DRAG = 0.7;
/** Off a branch: how hard you're thrown up, and how fast you come down. */
const JUMP = 6.5;
const GRAVITY = 26;
/** Ducks waddle across at this pace (units a second), and after a near miss stay away this long. */
const DUCK_SPEED = 2.2;
/** How far past the road's edge a duck wanders before turning back. */
const DUCK_WANDER = 6;
/** How much further on the second family at a crossing walks. */
const DUCK_GAP = 14;
export const DUCK_GONE = 5;
/** Skiers cross quicker, from further off, and further apart. */
const SKIER_SPEED = 7;
const SKIER_WANDER = 14;
const SKIER_GAP = 18;
/** Off a snow ramp's lip, you rise this much faster than you climbed it. */
const RAMP_KICK = 1.4;
/** Knock a snowman over and it's this long before it's been built again. */
export const SNOWMAN_GONE = 8;
/** Top speed up or down a hill: this much slower or faster for each unit of rise per unit of road. */
const HILL = 0.8;
/** On ice: how much the wheels bite when you turn, and how slowly a slide dies away. */
const ICE_GRIP = 0.6;
const ICE_DECAY = Math.exp(-3 * STEP);
const DRIFT_DECAY = Math.exp(-TUNING.drift.slideDecay * STEP);
/** How long a puddle keeps slowing you once you're out of it, and to what. */
const SOAK_TIME = 0.35;
const SOAK = 0.55;
/** How long slush keeps you sliding, as on ice, once you're off it. */
const SLIDE_TIME = 0.5;
/** How hard a croquet ball knocks you aside. */
const WHACK = 16;
const SIDES = [-1, 1];
/** How far across the road the computer drivers give each hazard (the moving ones they don't try to). */
const REACH: Record<HazardKind, number> = {
  pool: POOL_RADIUS, slush: SLUSH_WIDTH, swarm: SWARM_RADIUS, timber: TIMBER_WIDTH + KART_RADIUS, moose: 0, ball: 0, speaker: 0,
};
/** How long the smoke machine blows, how far its cloud reaches, and how much of their top speed it leaves the karts inside it. */
export const SMOKE_TIME = 5;
export const SMOKE_RADIUS = 9;
const SMOKE_SLOW = 0.55;
/** How long the Soundboks plays, the time between beats, and how far a beat reaches. */
export const BASS_TIME = 6;
export const BASS_BEAT = 60 / 140;
export const BASS_RADIUS = 11;
/** How hard a beat shoves you away, and how long it has you in the air. */
const BASS_PUSH = 9;
export const JOLT_TIME = 0.35;
/** How long the driver hangs over the side being sick. */
export const BARF_TIME = 1.1;
/** Through the sick: what's left of your speed, and how long you crawl after. */
const BARF_THUMP = 0.45;
const BARF_SOAK = 1.4;
/** Through the bearnaise: how long you slide, and how hard towards the wall. */
const GLIDE_TIME = 1.8;
const GLIDE = 15;
/** How close to a puddle on the road (beer, sick, sauce) you have to drive to be caught in it. */
const PUDDLE_REACH = 1.1;

export interface Racer {
  id: number;
  name: string;
  emoji: string;
  /** The roster character they drive (models.ts `characterOf`), if not the one they're named for. */
  char?: string;
}

export interface Kart extends Racer {
  player: boolean;
  x: number;
  z: number;
  heading: number;
  speed: number;
  steer: number;
  idx: number;
  lap: number;
  finishTime: number | null;
  place: number;
  item: ItemKind | null;
  /** Items waiting behind the one in the slot, next first (Zobbe's boxes hold four). */
  queue: ItemKind[];
  /** Seconds left of the item spinning in the slot, from a box, or 0. */
  roll: number;
  boost: number;
  /** How much faster than top speed the boost running now is. */
  boostMul: number;
  spin: number;
  /** Seconds left of not being hit again, after a twirl, or 0. */
  safe: number;
  /** Seconds left stalled on the line, after a start jumped too early, or 0. */
  stall: number;
  /** Drifting: which way (-1 left, 1 right, 0 not), for how long, and how long the steering's been held for one to start, or let go for one to end. */
  drift: number;
  charge: number;
  driftHold: number;
  driftLet: number;
  /** Seconds left to pull a trick, since leaving a lip or a branch; and the trick itself — seconds into it, 0 none yet, -1 passed up. */
  trick: number;
  tricked: number;
  /** On a boost pad this step: it's the moment you drive onto one that scores. */
  onPad: boolean;
  /** Seconds left of a jackpot frenzy (counting the reels still spinning), whether it's had one this race, and seconds till it can fire again. */
  frenzy: number;
  jackpot: boolean;
  cooldown: number;
  /** Seconds left as a moose, or 0. */
  moose: number;
  /** Seconds left itching from mosquito bites, or 0. */
  itch: number;
  /** Seconds left wet from a puddle, and sliding from slush. */
  soak: number;
  slide: number;
  /** Seconds left of the smoke machine blowing, or 0. */
  smoke: number;
  /** Seconds left of the Soundboks playing, or 0. */
  bass: number;
  /** Seconds left in the air, thrown up by a beat, or 0. */
  jolt: number;
  /** Seconds left leaning out being sick, or 0. */
  barf: number;
  /** Seconds left as a shopping trolley, or 0. */
  cart: number;
  /** Seconds left of the six-pack being fired, a can each CAN_GAP, or 0. */
  cans: number;
  /** The six-pack's being thrown backwards. */
  cansBack: boolean;
  /** Seconds tucked in behind another kart, for a slipstream. */
  slip: number;
  lane: number;
  laneTimer: number;
  itemTimer: number;
  /** Sideways velocity from being bumped, on top of driving forward. */
  kx: number;
  kz: number;
  /** Height off the road, off a branch, and how fast that's changing. */
  y: number;
  vy: number;
  /** Where it was at the start of the last step — drawn in between, for smooth motion. */
  px: number;
  py: number;
  pz: number;
  ph: number;
  ps: number;
}

/** A duck in a family crossing the road — the ducklings follow in their mother's steps — or a skier. */
export interface Duck {
  idx: number;
  skier?: boolean;
  /** Seconds behind the mother, and where in the walk the family started. */
  lag: number;
  phase: number;
  chick: boolean;
  /** Seconds left flown off after a kart came too close, or 0. */
  gone: number;
}

export interface Shot {
  kind: "beer" | "barf" | "bearnaise" | "glove" | "can" | "mosquito" | "rocket";
  x: number;
  z: number;
  vx: number;
  vz: number;
  idx: number;
  ttl: number;
  owner: number;
  target: number | null;
  /** Seconds the owner can't be hit by it — or it would hit them leaving. */
  grace: number;
  /** Where it was at the start of the last step. */
  px: number;
  pz: number;
}

/**
 * Something that happened, for whoever is listening — the HUD, the camera,
 * the balance runs. The sim only adds to the list; a listener empties it.
 */
export type RaceEvent = { t: number } & (
  /** Twirled (`spin`), or slowed or knocked, by a shot, a kart or the road. */
  | { kind: "hit"; kart: number; what: string; by: number | null; spin: boolean }
  | { kind: "land"; kart: number }
  /** An item from a box, and how far back the kart was (0 leading, 1 far behind). */
  | { kind: "box"; kart: number; item: ItemKind; back: number; reels: ItemKind[]; frenzy: boolean }
  /** Crossed the line: `lap` 0 is the start of the race. */
  | { kind: "lap"; kart: number; lap: number }
  | { kind: "finish"; kart: number }
  /** A drift's charge reached a new tier (`charge`), or was let go for that tier's boost (`drift`). */
  | { kind: "charge"; kart: number; tier: number }
  | { kind: "drift"; kart: number; tier: number }
  | { kind: "trick"; kart: number }
  | { kind: "pad"; kart: number }
  | { kind: "slip"; kart: number }
  /** A shot from behind stopped by the item trailing behind the kart. */
  | { kind: "block"; kart: number }
  /** The player overtook `passed`. */
  | { kind: "pass"; kart: number; passed: number }
  /** Points for the player: what for, how many, and the combo they were multiplied by. */
  | { kind: "score"; kart: number; what: string; points: number; mult: number }
  /** The player's start: timed right, or jumped too early. */
  | { kind: "start"; kart: number; good: boolean }
);

type Emitted = RaceEvent extends infer E ? (E extends RaceEvent ? Omit<E, "t"> : never) : never;

const emit = (race: Race, e: Emitted) => {
  const event = { ...e, t: raceTime(race) } as RaceEvent;
  race.events.push(event);
  score(race, event);
};

/** Points to the player for `what`, times the combo — which it then feeds — and the difficulty's multiplier. */
function award(race: Race, player: Kart, what: string, base: number) {
  const s = race.score;
  const { step, maxMult, window } = TUNING.combo;
  const mult = Math.min(1 + step * s.chain, maxMult);
  bank(race, player, what, Math.round(base * mult * TUNING.score.difficultyMult[race.difficulty]), mult);
  s.chain++;
  s.best = Math.max(s.best, s.chain);
  s.left = window;
}

function bank(race: Race, player: Kart, what: string, points: number, mult = 1) {
  const s = race.score;
  s.total += points;
  s.by[what] = (s.by[what] ?? 0) + points;
  race.events.push({ kind: "score", kart: player.id, what, points, mult, t: raceTime(race) });
}

/** The combo lost. */
const breakChain = (race: Race) => {
  race.score.chain = 0;
  race.score.left = 0;
};

/** Stars, 0 to 5, for a score — on this track's thresholds, if it has its own. */
export const starsFor = (total: number, thresholds = TUNING.stars) => thresholds.filter((at) => total >= at).length;

/** What the player scores for what just happened. */
function score(race: Race, e: RaceEvent) {
  const player = race.karts.find((k) => k.player);
  if (!player || race.autopilot) return;
  const S = TUNING.score;
  if (e.kind === "finish" && e.kart === player.id) {
    // Not multiplied: where you finish, and whether you beat your rival.
    const dm = S.difficultyMult[race.difficulty];
    bank(race, player, "finish", Math.round(S.finish[Math.min(player.place, S.finish.length) - 1] * dm));
    const rival = race.karts.find((k) => k.id === race.rival);
    if (rival && rival.finishTime === null) bank(race, player, "rival", Math.round(S.rival * dm));
    return;
  }
  if (player.finishTime !== null) return;
  if (e.kind === "hit" && e.kart === player.id) {
    if (e.spin) breakChain(race);
  } else if (e.kind === "hit" && e.by === player.id) award(race, player, "hit", e.spin ? S.hit : S.slow);
  else if (e.kart !== player.id) return;
  else if (e.kind === "drift") award(race, player, "drift", S.drift[e.tier - 1]);
  else if (e.kind === "trick") award(race, player, "trick", S.trick);
  else if (e.kind === "box") award(race, player, "box", S.box);
  else if (e.kind === "pad") award(race, player, "pad", S.pad);
  else if (e.kind === "slip") award(race, player, "slip", S.slip);
  else if (e.kind === "pass") {
    const last = race.score.passed.get(e.passed);
    race.score.passed.set(e.passed, e.t);
    if (last === undefined || e.t - last >= S.passAgain) award(race, player, "pass", S.pass);
  }
}

/** The player's points: the total, the combo running now, and where they came from. */
export interface Score {
  total: number;
  /** Things done in a row, and seconds left to do the next before the combo is lost. */
  chain: number;
  left: number;
  best: number;
  by: Record<string, number>;
  /** Seconds scraping a wall, and when each kart was last overtaken. */
  scrape: number;
  passed: Map<number, number>;
}

export interface Assists {
  steer: boolean;
  drift: boolean;
  item: boolean;
}

export interface Race {
  track: Track;
  difficulty: Difficulty;
  laps: number;
  /** Player drives on autopilot — the demo lap behind the menu. */
  autopilot: boolean;
  time: number;
  phase: "countdown" | "racing" | "done";
  karts: Kart[];
  shots: Shot[];
  boxes: { x: number; y: number; z: number; respawn: number }[];
  ducks: Duck[];
  /** Snowmen, and kubb blocks: all knocked over the same way. `r`: how close is touching. */
  snowmen: { idx: number; lat: number; x: number; y: number; z: number; gone: number; r: number; kubb?: "block" | "king" }[];
  /** Help the player's asked for: steering off walls, drifting, firing items. */
  assists: Assists;
  /** The computer driver out to beat the player — drawn at random at the start, and lined up just ahead of them — or null. */
  rival: number | null;
  /** Training: the player alone on the road. Nothing counts. */
  training: boolean;
  /** When the last rocket went up (race clock), so the next waits its turn. */
  rocketAt: number;
  /** When in the countdown the player first touched down, for the start boost, or null. */
  startPress: number | null;
  events: RaceEvent[];
  score: Score;
  rng: () => number;
}

export interface Input {
  steer: number;
  fire: boolean;
  /** A finger (or key) is down: in the countdown, that's the start boost. */
  held?: boolean;
  /**
   * The drift key, on a keyboard: held, a drift can start, and letting go
   * ends it. Left out (touch), holding full lock is what starts one.
   */
  drift?: boolean;
  /** A flick up (or ↑): a trick, if in the air off a lip or a branch. */
  trick?: boolean;
  /** A flick down: fire the item the other way — a glove or cans behind, beer or bearnaise lobbed ahead. */
  back?: boolean;
}

export function createRace(
  track: Track,
  racers: Racer[],
  playerId: number,
  difficulty: Difficulty,
  {
    laps = TUNING.laps,
    autopilot = false,
    rng = Math.random,
    assists = { steer: false, drift: false, item: false } as Assists,
    training = false,
    rival: pick = null as number | null,
  } = {},
): Race {
  // The player lines up last, Mario Kart style: the race is catching up. The
  // rival is drawn at random and lines up right in front of you. Training:
  // nobody but you.
  const others = training ? [] : racers.filter((r) => r.id !== playerId);
  const rival = autopilot || !others.length ? null : pick ?? others[Math.floor(rng() * others.length)].id;
  const picked = others.filter((r) => r.id === rival);
  const order = [...others.filter((r) => r.id !== rival), ...picked, ...racers.filter((r) => r.id === playerId)];
  const karts = order.map((r, k): Kart => {
    const idx = track.n - 5 - Math.floor(k / 2) * 5;
    const s = track.samples[idx];
    const lat = k % 2 ? 3 : -3;
    return {
      ...r,
      player: r.id === playerId,
      x: s.x + s.nx * lat,
      z: s.z + s.nz * lat,
      heading: Math.atan2(s.tz, s.tx),
      speed: 0,
      steer: 0,
      idx,
      // Behind the line: crossing it is what starts lap one.
      lap: -1,
      finishTime: null,
      place: k + 1,
      item: null,
      queue: [],
      roll: 0,
      boost: 0,
      boostMul: 1,
      spin: 0,
      safe: 0,
      stall: 0,
      drift: 0,
      charge: 0,
      driftHold: 0,
      driftLet: 0,
      trick: 0,
      tricked: 0,
      onPad: false,
      cansBack: false,
      slip: 0,
      frenzy: 0,
      jackpot: false,
      cooldown: 0,
      moose: 0,
      itch: 0,
      soak: 0,
      slide: 0,
      smoke: 0,
      bass: 0,
      jolt: 0,
      barf: 0,
      cart: 0,
      cans: 0,
      lane: lat,
      laneTimer: 0,
      kx: 0,
      kz: 0,
      y: 0,
      vy: 0,
      px: s.x + s.nx * lat,
      py: 0,
      pz: s.z + s.nz * lat,
      ph: Math.atan2(s.tz, s.tx),
      ps: 0,
      itemTimer: 0,
    };
  });
  return {
    track,
    difficulty,
    laps,
    autopilot,
    training,
    time: 0,
    phase: "countdown",
    karts,
    shots: [],
    boxes: track.boxes.map((b) => ({ ...b, respawn: 0 })),
    // Two families of a mother and three ducklings at each crossing, the
    // second a little further on and half a walk behind — so always going the
    // other way to the first — and each crossing out of step with the next.
    ducks: track.ducks.flatMap((idx, c) =>
      [0, 1].flatMap((other) =>
        [0, 1, 2, 3].map(
          (k): Duck => ({
            idx: (idx + other * DUCK_GAP) % track.n,
            lag: k * 0.45,
            phase: c * 7.3 + other * 2 * (track.half + DUCK_WANDER),
            chick: k > 0,
            gone: 0,
          }),
        ),
      ),
    ).concat(
      // A skier on their own at each of theirs, and another further on going the other way.
      track.skiers.flatMap((idx, c) =>
        [0, 1].map(
          (other): Duck => ({
            idx: (idx + other * SKIER_GAP) % track.n,
            skier: true,
            lag: 0,
            phase: c * 11.1 + other * 2 * (track.half + SKIER_WANDER),
            chick: false,
            gone: 0,
          }),
        ),
      ),
    ),
    snowmen: [
      ...track.snowmen.map((p) => ({ ...p, r: 0.9 })),
      ...track.kubbs.map(({ idx, lat, king }) => ({ idx, lat, r: king ? 0.8 : 0.55, kubb: king ? ("king" as const) : ("block" as const) })),
    ].map((man) => {
      const s = track.samples[man.idx];
      return { ...man, x: s.x + s.nx * man.lat, y: s.y, z: s.z + s.nz * man.lat, gone: 0 };
    }),
    assists,
    rival,
    startPress: null,
    rocketAt: -Infinity,
    events: [],
    score: { total: 0, chain: 0, left: 0, best: 0, by: {}, scrape: 0, passed: new Map() },
    rng,
  };
}

export const raceTime = (race: Race) => Math.max(0, race.time - COUNTDOWN);

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const wrapAngle = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));
const progress = (race: Race, k: Kart) => k.lap * race.track.n + k.idx;

/**
 * Where a duck is: walking to and fro across the road, waiting a moment on
 * the verge at either end — reckoned from the clock, so nothing to step.
 */
export function duckAt(race: Race, duck: Duck) {
  const { track } = race;
  const half = halfAt(track, duck.idx);
  const reach = half + (duck.skier ? SKIER_WANDER : DUCK_WANDER);
  const pace = duck.skier ? SKIER_SPEED : DUCK_SPEED;
  const u = ((((race.time - duck.lag) * pace + duck.phase) % (4 * reach)) + 4 * reach) % (4 * reach);
  const lat = clamp(Math.abs(u - 2 * reach) - reach, -(half + 2.5), half + 2.5);
  const s = track.samples[duck.idx];
  // Which way it's walking (or about to): across the road, one way or the other.
  const dir = u < 2 * reach ? -1 : 1;
  return { x: s.x + s.nx * lat, y: s.y, z: s.z + s.nz * lat, lat, dx: s.nx * dir, dz: s.nz * dir };
}

/**
 * How far back a kart is, 0 in the lead to 1 far behind: partly its place,
 * mostly how far it trails the leader — fifth on the leader's tail is in a
 * race; fifth half a lap down needs help.
 */
export function backness(race: Race, kart: Kart) {
  const { rankWeight, gapFull } = TUNING.items;
  const leader = race.karts.find((k) => k.place === 1) ?? kart;
  const rank = (kart.place - 1) / Math.max(1, race.karts.length - 1);
  const gap = clamp((progress(race, leader) - progress(race, kart)) / gapFull, 0, 1);
  return rankWeight * rank + (1 - rankWeight) * gap;
}

export function rollItem(race: Race, kart: Kart): ItemKind {
  // Further back, better luck: the front gets something to defend with, the
  // back something to catch up with — each item's chances blended between
  // the two by how far back you are.
  const { weights, cartFrom, mooseFrom } = TUNING.items;
  const back = backness(race, kart);
  const last = kart.place === race.karts.length && kart.place > 1;
  const R = TUNING.rocket;
  const leader = race.karts.find((k) => k.place === 1) ?? kart;
  const rocket =
    kart.place >= R.minPlace &&
    progress(race, leader) - progress(race, kart) >= R.minGap &&
    raceTime(race) - race.rocketAt >= R.cooldown &&
    !race.shots.some((s) => s.kind === "rocket");
  const table = Object.entries(weights).map(([kind, [front, far]]) => {
    // The trolley never for the leader; the moose only for whoever's last, and
    // well behind; the rocket only well behind, and one at a time.
    const allowed =
      kind === "cart" ? kart.place > 1 && back > cartFrom
      : kind === "moose" ? last && back > mooseFrom
      : kind === "rocket" ? rocket
      : true;
    return [kind as ItemKind, allowed ? Math.max(0, front + (far - front) * back) : 0] as const;
  });
  let roll = race.rng() * table.reduce((sum, [, w]) => sum + w, 0);
  for (const [kind, w] of table) {
    roll -= w;
    if (roll < 0) return kind;
  }
  return "glove";
}

/**
 * The three reels of a box, and whether they've hit the jackpot: all three
 * the same, and a frenzy with that item. Otherwise the item and two others —
 * now and then one of them the same, just to tease.
 */
function jackpot(race: Race, kart: Kart, back: number) {
  const F = TUNING.frenzy;
  const odds = (F.oddsFront + (F.oddsBack - F.oddsFront) * back) * (kart.player ? 1 : F.aiOdds);
  const item = kart.item!;
  if (!kart.jackpot && F.items.includes(item) && race.rng() < odds) {
    kart.jackpot = true;
    kart.queue.length = 0;
    kart.frenzy = F.time + kart.roll;
    return { reels: [item, item, item], frenzy: true };
  }
  // Zobbe's reels land on what's queued, so they don't change when the queue takes their place.
  if (kart.queue.length) return { reels: [...kart.queue], frenzy: false };
  const kinds = Object.keys(ITEMS).filter((k) => k !== item) as ItemKind[];
  const other = () => kinds[Math.floor(race.rng() * kinds.length)];
  const reels = race.rng() < F.nearMiss ? [item, item, other()] : [other(), item, other()];
  return { reels, frenzy: false };
}

/**
 * Whether the item in a kart's slot, trailing behind it, stops a shot: beer,
 * sick or bearnaise, ready to use, against a glove, cans or a swarm coming
 * from behind. (Not a rocket: that comes from above.)
 */
export const shields = (kart: Kart, shot: Shot) =>
  !!kart.item && isPuddleItem(kart.item) && kart.roll <= 0 && shot.kind !== "rocket" && !isPuddle(shot) &&
  (shot.x - kart.x) * Math.cos(kart.heading) + (shot.z - kart.z) * Math.sin(kart.heading) < 0;

/** An item that's dropped behind you as a puddle. */
export const isPuddleItem = (item: ItemKind) => item === "beer" || item === "barf" || item === "bearnaise";

/** Beer, sick or sauce: lying on the road, rather than flying along it. */
export const isPuddle = (shot: Shot) => shot.kind === "beer" || shot.kind === "barf" || shot.kind === "bearnaise";

/** Zobbe gets four different items from a box; everyone else, one. */
/**
 * Characters with a quirk of their own: Zobbe gets four items from a box.
 * (Nichlas's boat is his kart, in the roster.) The quirk goes with the
 * character, whoever's driving it.
 */
export const SPECIALS: Record<string, { box?: number }> = {
  zobbe: { box: 4 },
};

/** A racer's quirks, if the character they drive has any. */
export const specialOf = (r: Pick<Racer, "name" | "char">) =>
  SPECIALS[r.char ?? ""] ?? Object.entries(SPECIALS).find(([who]) => !r.char && r.name.toLowerCase().includes(who))?.[1] ?? {};

/** How many different items a box gives a kart. */
export const boxSize = (kart: Racer) => specialOf(kart).box ?? 1;

/**
 * A computer driver's own way of driving, the same every race — from its
 * member's id: how wobbly its line, how often it changes lane, how long it
 * sits on an item, and how daring it is about shortcuts. Each about 1.
 */
export function personality(id: number) {
  let found = personalities.get(id);
  if (found) return found;
  let h = Math.imul(id + 7, 2654435761) >>> 0;
  const next = () => {
    h = Math.imul(h ^ (h >>> 15), 2246822519) >>> 0;
    return 0.7 + 0.6 * ((h % 1000) / 1000);
  };
  found = { noise: next(), lane: next(), itemDelay: next(), daring: next() };
  personalities.set(id, found);
  return found;
}
const personalities = new Map<number, { noise: number; lane: number; itemDelay: number; daring: number }>();

/** A moose or a trolley: faster than anything, and nothing stops it. */
export const mighty = (kart: Kart) => kart.moose > 0 || kart.cart > 0;

/** Nothing can hurt it: a moose, a trolley, or in a frenzy. */
const immune = (kart: Kart) => mighty(kart) || kart.frenzy > 0;

/**
 * Twirl out: most of your speed gone, and no steering until you're round —
 * and a moment after it when nothing can twirl you again.
 */
function hit(race: Race, kart: Kart, what: string, by: number | null = null) {
  if (immune(kart) || kart.spin > 0 || kart.safe > 0) return;
  kart.spin = TUNING.spin.time;
  kart.safe = TUNING.spin.time + TUNING.spin.safe;
  endDrift(kart);
  kart.speed *= 0.3;
  kart.boost = 0;
  emit(race, { kind: "hit", kart: kart.id, what, by, spin: true });
}

/** Run into something small — a duck, a skier, a snowman, a kubb: no twirl, but most of your speed gone. */
function knock(race: Race, kart: Kart, what: string) {
  if (immune(kart)) return;
  kart.speed *= TUNING.knock;
  kart.boost = 0;
  emit(race, { kind: "hit", kart: kart.id, what, by: null, spin: false });
}

/** The best drift tier a kart can reach: the player all three; the computer, by difficulty. */
const maxTier = (race: Race, kart: Kart) =>
  !kart.player || race.autopilot ? TUNING.difficulty[race.difficulty].driftTier
  : race.assists.drift ? TUNING.assist.driftTier
  : TUNING.drift.tiers.length;

/** The tier a drift has charged to, 0 (none yet) up to the kart's best. */
export function driftTier(race: Race, kart: Kart) {
  if (!kart.drift) return 0;
  let reached = 0;
  for (const t of TUNING.drift.tiers) if (kart.charge >= t.charge) reached++;
  return Math.min(reached, maxTier(race, kart));
}

/** A drift over, without its boost. */
function endDrift(kart: Kart) {
  kart.drift = kart.charge = kart.driftHold = kart.driftLet = 0;
}

/**
 * A drift, step by step: starting one when the steering's held hard over,
 * charging it, and letting it go for its tier's boost when the steering
 * comes back. `hard`: how far over the steering must be to start one; `key`:
 * the keyboard's drift key, if that's what's driving it.
 */
function updateDrift(race: Race, kart: Kart, steer: number, hard: number, key?: boolean, release = TUNING.drift.releaseSteer) {
  const D = TUNING.drift;
  const quick = kart.speed >= D.minSpeed * MAX_SPEED;
  if (!kart.drift) {
    const want = key === undefined ? Math.abs(steer) >= hard : key && Math.abs(steer) > 0.5;
    kart.driftHold = want && quick && kart.spin <= 0 && kart.y <= 0 ? kart.driftHold + STEP : 0;
    if (kart.driftHold >= D.enterHold) {
      kart.drift = Math.sign(steer);
      kart.charge = kart.driftLet = 0;
    }
    return;
  }
  // Slowed right down, or twirled: it's over.
  if (kart.speed < D.minSpeed * MAX_SPEED * 0.7 || kart.spin > 0) return endDrift(kart);
  const was = driftTier(race, kart);
  kart.charge += STEP;
  const tier = driftTier(race, kart);
  if (tier > was) emit(race, { kind: "charge", kart: kart.id, tier });
  kart.driftLet = key === false || steer * kart.drift < release ? kart.driftLet + STEP : 0;
  if (kart.driftLet < D.releaseHold) return;
  // Let go: the boost — though a computer driver's sometimes comes to nothing.
  const skip = !kart.player && race.rng() < TUNING.ai.driftSkip;
  if (tier && !skip) {
    giveBoost(kart, D.tiers[tier - 1]);
    emit(race, { kind: "drift", kart: kart.id, tier });
  }
  endDrift(kart);
}

/** A boost of `time` seconds at `mul` times top speed — unless a stronger one is already running. */
function giveBoost(kart: Kart, { time, mul }: { time: number; mul: number }) {
  if (kart.boost > 0 && kart.boostMul > mul) return;
  kart.boost = Math.max(kart.boost, time);
  kart.boostMul = mul;
}

/** Something thrown from a kart: `ahead` of it (behind, if negative), at `speed` the way it's facing. */
function launch(race: Race, kart: Kart, kind: Shot["kind"], ahead: number, speed: number, target: number | null = null) {
  const fx = Math.cos(kart.heading);
  const fz = Math.sin(kart.heading);
  race.shots.push({
    kind,
    x: kart.x + fx * ahead,
    z: kart.z + fz * ahead,
    px: kart.x + fx * ahead,
    pz: kart.z + fz * ahead,
    vx: fx * speed,
    vz: fz * speed,
    idx: kart.idx,
    ttl: kind === "glove" ? 4 : kind === "can" ? CAN_TIME : kind === "mosquito" ? 8 : kind === "rocket" ? 20 : 90,
    owner: kart.id,
    target,
    grace: 0.4,
  });
}

/** Thrown as the glove is, a little slower — or behind you. */
const throwCan = (race: Race, kart: Kart) =>
  kart.cansBack ? launch(race, kart, "can", -2.2, -20) : launch(race, kart, "can", 2, Math.max(kart.speed, MAX_SPEED) + 20);

/** Use the item in the slot; `back`: the other way to usual (see Input.back). */
export function fireItem(race: Race, kart: Kart, back = false) {
  const item = kart.item;
  if (!item || kart.spin > 0 || kart.roll > 0 || kart.cooldown > 0) return;
  // In a frenzy it never runs out — only needs a moment between goes.
  if (kart.frenzy > 0) kart.cooldown = item === "tuborg" ? TUNING.frenzy.tuborgCooldown : TUNING.frenzy.fireCooldown;
  else kart.item = kart.queue.shift() ?? null;
  const shot = (kind: Shot["kind"], ahead: number, speed: number, target: number | null = null) =>
    launch(race, kart, kind, ahead, speed, target);
  if (item === "energy" || item === "jager") giveBoost(kart, TUNING.boost.item);
  else if (item === "moose") kart.moose = TUNING.moose.time;
  else if (item === "cart") kart.cart = TUNING.cart.time;
  else if (item === "beer" || item === "bearnaise") {
    if (back) {
      // Lobbed ahead, and no slipping in it yourself as you catch it up.
      shot(item, TUNING.lob.ahead, 0);
      race.shots[race.shots.length - 1].grace = TUNING.lob.grace;
    } else shot(item, -2.4, 0);
  }
  else if (item === "barf") {
    kart.barf = BARF_TIME;
    shot("barf", -2.4, 0);
  }
  else if (item === "glove") {
    if (back) shot("glove", -2.2, -26);
    else shot("glove", 2, Math.max(kart.speed, MAX_SPEED) + 26);
  } else if (item === "tuborg") {
    kart.cansBack = back;
    throwCan(race, kart);
    kart.cans = (CANS - 1) * CAN_GAP;
  }
  else if (item === "rocket") {
    race.rocketAt = raceTime(race);
    const leader = race.karts.find((k) => k.place === 1)!;
    shot("rocket", 2, TUNING.rocket.speed, leader.id);
  } else if (item === "smoke") kart.smoke = SMOKE_TIME;
  else if (item === "soundboks") kart.bass = BASS_TIME;
  else {
    const ahead = race.karts.find((k) => k.place === kart.place - 1);
    shot("mosquito", 2, 36, ahead ? ahead.id : null);
  }
}

/**
 * Keep a point on the road or on a shortcut through the bushes; returns the
 * wall it hit — its inward normal, and the way it runs — if it hit one.
 */
function confine(track: Track, obj: { x: number; z: number; idx: number }, margin: number) {
  const at = locate(track, obj.x, obj.z, obj.idx);
  const over = Math.abs(at.lat) - (halfAt(track, at.idx) - margin);
  const near = nearestCut(track, obj.x, obj.z);
  const cutOver = near ? near.dist - (near.half - margin) : Infinity;
  obj.idx = at.idx;
  if (over <= 0) return null;
  if (near && cutOver <= over) {
    // In the bushes, it's as far round the lap as it is along the path.
    obj.idx = (near.cut.a + Math.round(near.t * near.cut.skip)) % track.n;
    if (cutOver <= 0) return null;
    const nx = (near.cx - obj.x) / near.dist;
    const nz = (near.cz - obj.z) / near.dist;
    obj.x += nx * cutOver;
    obj.z += nz * cutOver;
    return { nx, nz, tx: near.cut.dx, tz: near.cut.dz };
  }
  const s = track.samples[at.idx];
  const side = Math.sign(at.lat);
  obj.x -= s.nx * side * over;
  obj.z -= s.nz * side * over;
  return { nx: -side * s.nx, nz: -side * s.nz, tx: s.tx, tz: s.tz };
}

/**
 * After a kart has moved — by driving or by being shoved — keep it inside
 * the walls and keep its lap count honest.
 */
function settle(race: Race, kart: Kart, driving: boolean) {
  const { track } = race;
  const before = kart.idx;
  const wall = confine(track, kart, KART_RADIUS);
  if (wall) {
    // A knock into the wall stops at the wall.
    const into = kart.kx * wall.nx + kart.kz * wall.nz;
    if (into < 0) {
      kart.kx -= into * wall.nx;
      kart.kz -= into * wall.nz;
    }
    if (driving) {
      // A drift into the wall is a drift thrown away — the player's: a
      // computer driver's drift is only a reward for its line, not a test of it.
      if (kart.player) {
        endDrift(kart);
        // Scraping along it long enough loses the combo.
        if ((race.score.scrape += STEP) >= TUNING.combo.scrape) breakChain(race);
      }
      // Turn to run with the wall rather than into it, losing speed for how
      // square on you hit it — a head-on hit costs a lot, a graze next to
      // nothing — and a little more for scraping along it.
      const square = Math.max(0, -(Math.cos(kart.heading) * wall.nx + Math.sin(kart.heading) * wall.nz));
      kart.speed *= Math.max(0, 1 - TUNING.wall.impact * square * square - TUNING.wall.scrape * STEP);
      const along = Math.atan2(wall.tz, wall.tx);
      kart.heading = along + wrapAngle(kart.heading - along) * 0.6;
    }
  }
  if (!wall && kart.player) race.score.scrape = 0;
  const d = kart.idx - before;
  if (d < -track.n / 2) {
    kart.lap++;
    emit(race, { kind: "lap", kart: kart.id, lap: kart.lap });
  } else if (d > track.n / 2) kart.lap--;
  if (kart.lap >= race.laps && kart.finishTime === null && race.phase !== "countdown") {
    kart.finishTime = raceTime(race);
    emit(race, { kind: "finish", kart: kart.id });
  }
}

/**
 * Two karts touching: push them apart, then trade momentum along the line
 * between them like two bumper cars of equal weight. Whatever lands along a
 * kart's nose changes its speed — a rear-ender slows the one behind and
 * nudges the one in front — and whatever lands sideways is a knock that
 * slides it across the road for a moment.
 */
export function bump(race: Race, a: Kart, b: Kart) {
  if (Math.hypot(b.x - a.x, b.z - a.z) > 2 * (BUMP_OFFSET + BUMP_RADIUS)) return false;
  // Each kart is two circles, nose and tail; the deepest-overlapping pair
  // decides where they touched. (Plain loops: this runs for every close pair, every step.)
  const afx = Math.cos(a.heading) * BUMP_OFFSET;
  const afz = Math.sin(a.heading) * BUMP_OFFSET;
  const bfx = Math.cos(b.heading) * BUMP_OFFSET;
  const bfz = Math.sin(b.heading) * BUMP_OFFSET;
  let depth = 0;
  let nx = 0;
  let nz = 0;
  for (let i = 1; i >= -1; i -= 2) {
    const ax = a.x + afx * i;
    const az = a.z + afz * i;
    for (let j = 1; j >= -1; j -= 2) {
      const bx = b.x + bfx * j;
      const bz = b.z + bfz * j;
      const d = Math.hypot(bx - ax, bz - az);
      if (d > 0 && 2 * BUMP_RADIUS - d > depth) {
        depth = 2 * BUMP_RADIUS - d;
        nx = (bx - ax) / d;
        nz = (bz - az) / d;
      }
    }
  }
  if (depth <= 0) return false;

  a.x -= nx * depth * 0.5;
  a.z -= nz * depth * 0.5;
  b.x += nx * depth * 0.5;
  b.z += nz * depth * 0.5;

  const velocity = (k: Kart) => [Math.cos(k.heading) * k.speed + k.kx, Math.sin(k.heading) * k.speed + k.kz];
  const [avx, avz] = velocity(a);
  const [bvx, bvz] = velocity(b);
  const closing = (bvx - avx) * nx + (bvz - avz) * nz;
  if (closing < 0) {
    const j = (-(1 + BUMP_RESTITUTION) * closing) / 2;
    const give = (k: Kart, dx: number, dz: number) => {
      const fx = Math.cos(k.heading);
      const fz = Math.sin(k.heading);
      const along = dx * fx + dz * fz;
      const speed = k.speed + along;
      // Can't be knocked into reverse: what would, becomes knock instead.
      k.speed = Math.max(0, speed);
      k.kx += dx - along * fx + Math.min(0, speed) * fx;
      k.kz += dz - along * fz + Math.min(0, speed) * fz;
    };
    // Blowing smoke, you plough through the karts slowed in it: they take the whole knock.
    const [ka, kb] = (a.smoke > 0) === (b.smoke > 0) ? [1, 1] : a.smoke > 0 ? [0, 2] : [2, 0];
    give(a, -j * nx * ka, -j * nz * ka);
    give(b, j * nx * kb, j * nz * kb);
  }
  if (mighty(a) && !mighty(b)) hit(race, b, a.moose > 0 ? "moose" : "cart", a.id);
  if (mighty(b) && !mighty(a)) hit(race, a, b.moose > 0 ? "moose" : "cart", b.id);
  return true;
}

/** Someone other than `kart`, not a moose, within `radius` of it. */
const near = (race: Race, kart: Kart, radius: number) =>
  race.karts.some((k) => k !== kart && !mighty(k) && Math.hypot(k.x - kart.x, k.z - kart.z) < radius);

/** A beat of bass from a point: everyone within its reach, bar `except`, is thrown up and away from it. */
function bassPush(race: Race, x: number, z: number, except: Kart | null) {
  for (const k of race.karts) {
    if (k === except || immune(k)) continue;
    const dx = k.x - x;
    const dz = k.z - z;
    const d = Math.hypot(dx, dz);
    if (d >= BASS_RADIUS || d === 0) continue;
    const push = BASS_PUSH * (1 - (d / BASS_RADIUS) * 0.5);
    k.kx += (dx / d) * push;
    k.kz += (dz / d) * push;
    k.speed *= 0.8;
    k.jolt = JOLT_TIME;
  }
}

/** A beat from `kart`'s Soundboks: everyone near is thrown up and away. */
function thump(race: Race, kart: Kart) {
  // Anything flying near — a glove, cans, a swarm, a rocket — knocked out of the air.
  let kept = 0;
  for (const shot of race.shots) {
    if (!isPuddle(shot) && shot.owner !== kart.id && Math.hypot(shot.x - kart.x, shot.z - kart.z) < BASS_RADIUS) continue;
    race.shots[kept++] = shot;
  }
  race.shots.length = kept;
  bassPush(race, kart.x, kart.z, kart);
}

export function aiSteer(race: Race, kart: Kart) {
  const { track } = race;
  const diff = TUNING.difficulty[race.difficulty];
  kart.laneTimer -= STEP;
  if (kart.laneTimer <= 0) {
    kart.laneTimer = (1.5 + race.rng() * 3) * personality(kart.id).lane;
    kart.lane = (race.rng() * 2 - 1) * (narrowest(track, kart.idx, 60) - 2.5);
    // Empty-handed: aim for a box in the next row, if one is coming up.
    if (!kart.item) {
      const box = race.boxes.find((b) => {
        if (b.respawn > 0) return false;
        const at = locate(track, b.x, b.z, kart.idx);
        const d = loopDelta(kart.idx, at.idx, track.n);
        return d > 6 && d < 40;
      });
      if (box) kart.lane = locate(track, box.x, box.z, kart.idx).lat;
    }
  }
  const look = Math.round(5 + kart.speed * 0.35);
  const s = track.samples[(kart.idx + look) % track.n];
  // The room to keep to: the narrowest the road gets between here and where it's looking.
  const room = narrowest(track, kart.idx, look) - 1.5;
  // Someone just ahead in my line: go round them, on the side with more road.
  let lane = kart.lane;
  const here = track.samples[kart.idx];
  const myLat = (kart.x - here.x) * here.nx + (kart.z - here.z) * here.nz;
  for (const other of race.karts) {
    if (other === kart) continue;
    const ahead = loopDelta(kart.idx, other.idx, track.n);
    const lat = (other.x - here.x) * here.nx + (other.z - here.z) * here.nz;
    if (ahead > 0 && ahead < 7 && Math.abs(lat - myLat) < 2 && other.speed < kart.speed + 1) {
      lane = clamp(lat + (lat > 0 ? -2.6 : 2.6), -room, room);
      break;
    }
  }
  // Ducks in my line: round them too, on the side they're walking away from.
  for (const duck of race.ducks) {
    const ahead = loopDelta(kart.idx, duck.idx, track.n);
    if (duck.gone > 0 || ahead < 2 || ahead > 16) continue;
    const { lat } = duckAt(race, duck);
    if (Math.abs(lat) < halfAt(track, duck.idx) && Math.abs(lat - lane) < 2.5) {
      lane = clamp(lat + (lat > 0 ? -3.5 : 3.5), -room, room);
      break;
    }
  }
  // And round snowmen, on the side with more road.
  for (const man of race.snowmen) {
    const ahead = loopDelta(kart.idx, man.idx, track.n);
    if (man.gone > 0 || ahead < 2 || ahead > 18 || Math.abs(man.lat - lane) > 2.8) continue;
    lane = clamp(man.lat + (man.lat > 0 ? -3.8 : 3.8), -room, room);
    break;
  }
  lane = clamp(lane, -room, room);
  if (race.difficulty !== "easy") lane = dodge(race, kart, lane);
  let tx = s.x + s.nx * lane;
  let tz = s.z + s.nz * lane;
  const cut = shortcutFor(race, kart);
  if (cut) {
    // Going for a shortcut: to the gap in the wall, then through it.
    const gap = loopDelta(kart.idx, cut.a, track.n);
    tx = gap > 3 ? cut.ax : cut.ax + cut.dx * 10;
    tz = gap > 3 ? cut.az : cut.az + cut.dz * 10;
  }
  if (Math.abs(myLat) > halfAt(track, kart.idx)) {
    // Knocked into a shortcut: follow it through to the road again.
    const near = nearestCut(track, kart.x, kart.z);
    if (near) {
      const ahead = Math.min(near.cut.len + 6, near.t * near.cut.len + 8);
      tx = near.cut.ax + near.cut.dx * ahead;
      tz = near.cut.az + near.cut.dz * ahead;
    }
  }
  const want = wrapAngle(Math.atan2(tz - kart.z, tx - kart.x) - kart.heading);
  const wobble = Math.sin(race.time * 1.3 + kart.id * 1.7) * diff.noise * personality(kart.id).noise;
  return clamp(want * 3 + wobble, -1, 1);
}

/**
 * The shortcut a computer driver's going for, if one's coming up and it's
 * decided to — once a lap for each, by its difficulty and its nerve.
 */
function shortcutFor(race: Race, kart: Kart, reach = 30) {
  const item = kart.item === "energy" || kart.item === "jager";
  const chance = TUNING.difficulty[race.difficulty].shortcut * personality(kart.id).daring;
  if (!chance) return null;
  for (const cut of race.track.cuts) {
    const gap = loopDelta(kart.idx, cut.a, race.track.n);
    // Slow through the bushes: only with a drink to fire at the gate, or a boost that lasts till then.
    if (gap < 0 || gap > reach || (cut.speed < 1 && !item && kart.boost * kart.speed < gap)) continue;
    const roll = ((Math.imul(kart.id + 1, 73856093) ^ Math.imul(cut.a + 1, 19349663) ^ Math.imul(kart.lap + 3, 83492791)) >>> 0) % 1000;
    if (roll < chance * 1000) return cut;
  }
  return null;
}

/** Through the next croquet hoop, round the next puddle, slush, swarm or timber stack. */
function dodge(race: Race, kart: Kart, lane: number) {
  const { track } = race;
  const limit = narrowest(track, kart.idx, 40) - 1.5;
  if (track.map.wickets) {
    for (const pad of track.pads) {
      const ahead = loopDelta(kart.idx, pad.idx, track.n);
      if (ahead > 0 && ahead < 30) return pad.lat;
    }
  }
  // Timber first, and from further off: it's the one thing that stops you dead.
  let next: Hazard | null = null;
  let nearest = Infinity;
  for (const h of track.hazards) {
    if (h.kind === "moose" || h.kind === "ball" || h.kind === "speaker") continue;
    const ahead = loopDelta(kart.idx, h.idx, track.n);
    const timber = h.kind === "timber";
    if (next?.kind === "timber" && !timber) continue;
    if (ahead <= (timber ? -TIMBER_LENGTH - 1 : -2) || ahead >= (timber ? 40 : 28)) continue;
    if (ahead < nearest || (timber && next?.kind !== "timber")) {
      nearest = ahead;
      next = h;
    }
  }
  if (!next) return lane;
  const at = hazardLat(next, race.time);
  const reach = REACH[next.kind] + 1.5;
  if (Math.abs(lane - at) >= reach) return lane;
  // The side with more road.
  return clamp(at > 0 ? at - reach : at + reach, -limit, limit);
}

/**
 * What the road does to a kart: puddles, slush, mosquitoes, timber, moose
 * and croquet balls. Measured along and across the road from each one, as the
 * pads are.
 */
function hazards(race: Race, kart: Kart) {
  const { track } = race;
  const s = track.samples[kart.idx];
  const lat = (kart.x - s.x) * s.nx + (kart.z - s.z) * s.nz;
  const grounded = kart.y <= 0;
  for (const h of track.hazards) {
    const along = loopDelta(h.idx, kart.idx, track.n);
    if (Math.abs(along) > 6) continue;
    const at = hazardLat(h, race.time);
    const off = lat - at;
    const d = Math.hypot(along, off);
    if (h.kind === "pool") {
      if (grounded && d < POOL_RADIUS) kart.soak = SOAK_TIME;
    } else if (h.kind === "slush") {
      if (grounded && (along / SLUSH_LENGTH) ** 2 + (off / SLUSH_WIDTH) ** 2 < 1) kart.slide = SLIDE_TIME;
    } else if (h.kind === "swarm") {
      if (d < SWARM_RADIUS && !immune(kart)) kart.itch = ITCH_TIME;
    } else if (h.kind === "timber") {
      // Solid: pushed out the shorter way — off its end, stopped nearly dead;
      // off its side, a scrape.
      const exact = along + (kart.x - s.x) * s.tx + (kart.z - s.z) * s.tz;
      const intoEnd = TIMBER_LENGTH + KART_RADIUS - Math.abs(exact);
      const intoSide = TIMBER_WIDTH + KART_RADIUS - Math.abs(off);
      if (intoEnd <= 0 || intoSide <= 0) continue;
      if (intoEnd < intoSide) {
        // Bounced back, and glanced off towards the open side of the road —
        // or you'd crawl into it again and again, too slow to steer away.
        const dir = Math.sign(exact) || -1;
        const open = h.lat > 0 ? -1 : 1;
        kart.x += s.tx * dir * intoEnd;
        kart.z += s.tz * dir * intoEnd;
        kart.speed *= 0.25;
        kart.boost = 0;
        kart.kx += s.nx * open * 8 + s.tx * dir * 3;
        kart.kz += s.nz * open * 8 + s.tz * dir * 3;
      } else {
        const dir = Math.sign(off) || 1;
        kart.x += s.nx * dir * intoSide;
        kart.z += s.nz * dir * intoSide;
        kart.speed *= 0.9;
      }
    } else if (h.kind === "speaker") {
      // Stands by the road; it acts on the beat (see `step`).
      continue;
    } else if (h.kind === "moose") {
      if (d < MOOSE_RADIUS + KART_RADIUS && kart.y < 1.4) hit(race, kart, "moose");
    } else if (d < BALL_RADIUS + KART_RADIUS && kart.y < 1.4 && !immune(kart)) {
      // Knocked the way the ball is rolling — once, not every step it's touching.
      const dir = Math.sign(hazardLat(h, race.time + STEP) - at) || Math.sign(off) || 1;
      if ((kart.kx * s.nx + kart.kz * s.nz) * dir < WHACK / 2) {
        kart.kx += s.nx * dir * WHACK;
        kart.kz += s.nz * dir * WHACK;
        kart.speed *= 0.6;
      }
    }
  }
}

export function aiItems(race: Race, kart: Kart) {
  if (!kart.item) return;
  kart.itemTimer -= STEP;
  // A rocket coming for it, and a Soundboks to hand: now, whatever the wait.
  const incoming = kart.item === "soundboks" && kart.roll <= 0 && race.difficulty !== "easy" &&
    race.shots.some((s) => s.kind === "rocket" && s.target === kart.id && Math.hypot(s.x - kart.x, s.z - kart.z) < BASS_RADIUS * 2);
  if (incoming) {
    fireItem(race, kart);
    return;
  }
  if (kart.item === "energy" || kart.item === "jager") {
    // Going for a shortcut that's slow without a boost: keep it for the gate.
    const cut = shortcutFor(race, kart, 150);
    if (cut && cut.speed < 1) {
      if (loopDelta(kart.idx, cut.a, race.track.n) <= 4) fireItem(race, kart);
      return;
    }
  }
  if (kart.itemTimer > 0) return;
  if (isPuddleItem(kart.item) && race.difficulty !== "easy" && kart.itemTimer > -6) {
    // Something to drop behind: kept for someone close on its tail — for a
    // while, then dropped anyway.
    const fx = Math.cos(kart.heading);
    const fz = Math.sin(kart.heading);
    const chased = race.karts.some((k) => {
      const dx = k.x - kart.x;
      const dz = k.z - kart.z;
      const d = Math.hypot(dx, dz);
      return k !== kart && d < 15 && (dx * fx + dz * fz) / d < -0.8;
    });
    if (!chased) return;
  }
  if (kart.item === "smoke" || kart.item === "soundboks") {
    // Nobody close enough to catch it: wait until there is.
    if (race.difficulty !== "easy" && !near(race, kart, (kart.item === "smoke" ? SMOKE_RADIUS : BASS_RADIUS) * 0.7)) {
      kart.itemTimer = 0.4;
      return;
    }
  } else if ((kart.item === "glove" || kart.item === "tuborg") && (race.difficulty !== "easy" || kart.id === race.rival)) {
    // Hold a straight shot until there's someone in front to aim it at — the
    // rival, only the player, while the player is close ahead.
    const fx = Math.cos(kart.heading);
    const fz = Math.sin(kart.heading);
    const player = race.karts.find((k) => k.player);
    const gap = player ? progress(race, player) - progress(race, kart) : 0;
    const only = kart.id === race.rival && gap > 0 && gap < 60 ? player : null;
    const target = race.karts.some((k) => {
      if (k === kart || (only && k !== only)) return false;
      const dx = k.x - kart.x;
      const dz = k.z - kart.z;
      const d = Math.hypot(dx, dz);
      return d < 35 && (dx * fx + dz * fz) / d > 0.93;
    });
    if (!target) {
      // Nobody ahead, but someone right on its tail: a glove behind instead.
      const chaser = kart.item === "glove" && race.karts.some((k) => k !== kart && (k.x - kart.x) * fx + (k.z - kart.z) * fz < 0 && Math.hypot(k.x - kart.x, k.z - kart.z) < 12);
      if (!chaser) {
        kart.itemTimer = 0.4;
        return;
      }
      fireItem(race, kart, true);
      return;
    }
  }
  fireItem(race, kart);
  // More of it left: the next one in a moment.
  if (kart.item) kart.itemTimer = 0.8;
}

/**
 * The rubber band on a computer kart, as a share of its top speed: nothing
 * while it's near the player, then quicker the further it's behind them and
 * slower the further ahead — the rival measured from a little way in front of
 * the player, where it wants to be. `playerLead`: how far the player is ahead of it.
 */
export function rubberBand(race: Race, kart: Kart, playerLead: number, fade = 1) {
  const diff = TUNING.difficulty[race.difficulty];
  const { deadZone, fullAt } = TUNING.rubber;
  const lead = playerLead + (kart.id === race.rival ? TUNING.rival.targetAhead : 0);
  const pull = clamp((Math.abs(lead) - deadZone) / (fullAt - deadZone), 0, 1) * fade;
  if (lead <= 0) return -pull * diff.slowDown;
  const leading = (race.karts.find((k) => k.player)?.place ?? 1) <= 2;
  return pull * diff.catchUp * (leading ? 1 : TUNING.rubber.trailing);
}

/**
 * The steering assist: near a wall and heading into it, a nudge away from it
 * — the nearer, the harder. Positive steering turns towards the road's right.
 */
function wallAssist(race: Race, kart: Kart) {
  const { wallMargin, strength } = TUNING.assist;
  const s = race.track.samples[kart.idx];
  const lat = (kart.x - s.x) * s.nx + (kart.z - s.z) * s.nz;
  const near = clamp((Math.abs(lat) - (halfAt(race.track, kart.idx) - wallMargin)) / wallMargin, 0, 1);
  const towards = (Math.cos(kart.heading) * s.nx + Math.sin(kart.heading) * s.nz) * Math.sign(lat);
  return near > 0 && towards > 0 ? -Math.sign(lat) * strength * near : 0;
}

function driveKart(race: Race, kart: Kart, input: Input, playerLead: number, fade: number) {
  const { track } = race;
  const diff = TUNING.difficulty[race.difficulty];
  const manual = kart.player && !race.autopilot && kart.finishTime === null;

  let steer = manual ? clamp(input.steer, -1, 1) : aiSteer(race, kart);
  if (manual && (race.assists.steer || kart.moose > 0) && !kart.drift) steer = clamp(steer + wallAssist(race, kart), -1, 1);
  // Bitten: hard to hold a line while swatting.
  if (kart.itch > 0) steer = clamp(steer + Math.sin(race.time * 17 + kart.id) * 0.6, -1, 1);
  kart.steer += (steer - kart.steer) * Math.min(1, STEP * 12);
  // The player drifts by hand; a computer driver's drift is only its reward
  // for taking a corner hard — its line is its own either way.
  if (manual) updateDrift(race, kart, steer, race.assists.drift ? TUNING.assist.driftSteer : TUNING.drift.enterSteer, input.drift);
  else if (maxTier(race, kart) > 0) updateDrift(race, kart, kart.steer, TUNING.ai.driftSteer, undefined, TUNING.ai.driftRelease);
  if (!manual || race.assists.item) aiItems(race, kart);

  kart.boost = Math.max(0, kart.boost - STEP);
  kart.spin = Math.max(0, kart.spin - STEP);
  kart.safe = Math.max(0, kart.safe - STEP);
  kart.stall = Math.max(0, kart.stall - STEP);
  kart.itch = Math.max(0, kart.itch - STEP);
  kart.soak = Math.max(0, kart.soak - STEP);
  kart.slide = Math.max(0, kart.slide - STEP);
  kart.roll = Math.max(0, kart.roll - STEP);
  kart.moose = Math.max(0, kart.moose - STEP);
  kart.smoke = Math.max(0, kart.smoke - STEP);
  kart.jolt = Math.max(0, kart.jolt - STEP);
  kart.barf = Math.max(0, kart.barf - STEP);
  kart.cart = Math.max(0, kart.cart - STEP);
  kart.cooldown = Math.max(0, kart.cooldown - STEP);
  if (kart.frenzy > 0 && (kart.frenzy -= STEP) <= 0) {
    // The frenzy's over, and the item with it.
    kart.frenzy = 0;
    kart.item = null;
  }
  if (kart.bass > 0) {
    // A beat each time the time left crosses a whole number of them.
    const beats = Math.ceil(kart.bass / BASS_BEAT);
    kart.bass = Math.max(0, kart.bass - STEP);
    if (Math.ceil(kart.bass / BASS_BEAT) < beats) thump(race, kart);
  }
  if (kart.cans > 0) {
    // The rest of the six-pack, a can each time the time left crosses a whole number of gaps.
    const left = Math.ceil(kart.cans / CAN_GAP - 1e-6);
    kart.cans = Math.max(0, kart.cans - STEP);
    if (Math.ceil(kart.cans / CAN_GAP - 1e-6) < left && kart.spin <= 0) throwCan(race, kart);
  }
  if (kart.moose > 0) {
    // Back to a kart as the moose closes in on the lead.
    const leader = race.karts.find((k) => k.place === 1)!;
    const gap = progress(race, leader) - progress(race, kart);
    if (kart.place === 1 || (kart.place === 2 && gap < TUNING.moose.catchGap)) kart.moose = 0;
  }

  // Tucked in behind someone long enough: pulled past on their slipstream.
  const S = TUNING.slipstream;
  const fx = Math.cos(kart.heading);
  const fz = Math.sin(kart.heading);
  let drafting = false;
  if (kart.speed > 12 && kart.y <= 0) {
    for (const k of race.karts) {
      const dx = k.x - kart.x;
      const dz = k.z - kart.z;
      const d = Math.hypot(dx, dz);
      if (k !== kart && d > 0 && d < S.range && (dx * fx + dz * fz) / d > S.cone) drafting = true;
    }
  }
  kart.slip = drafting ? kart.slip + STEP : 0;
  if (kart.slip >= S.charge) {
    kart.slip = 0;
    giveBoost(kart, S.boost);
    emit(race, { kind: "slip", kart: kart.id });
  }

  let top = MAX_SPEED;
  if (!kart.player || race.autopilot) {
    // Rubber band: a little quicker when well behind the player, a little
    // slower when well clear — enough to keep the pack in sight, not to cheat,
    // and never faster than the cap.
    const rival = kart.id === race.rival ? TUNING.rival.speed : 1;
    top = Math.min(top * diff.speed * rival * (1 + rubberBand(race, kart, playerLead, fade)), MAX_SPEED * TUNING.rubber.maxTop);
  }
  if (kart.boost > 0) top *= kart.boostMul;
  if (kart.cart > 0) top = Math.max(top, MAX_SPEED * TUNING.cart.speed);
  if (kart.moose > 0) top = MAX_SPEED * TUNING.moose.speed;
  if (kart.itch > 0) top *= 0.6;
  if (kart.soak > 0) top *= SOAK;
  let smoked = false;
  for (const k of race.karts) if (k !== kart && k.smoke > 0 && Math.hypot(k.x - kart.x, k.z - kart.z) < SMOKE_RADIUS) smoked = true;
  if (!immune(kart) && smoked) {
    top *= SMOKE_SLOW;
  }
  if (kart.drift && manual) top *= 1 - TUNING.drift.speedLoss;
  if (kart.spin > 0 || kart.stall > 0) top = 0;
  if (kart.y <= 0 && inWater(track, kart.x, kart.z)) top *= WATER_DRAG;
  if (kart.y <= 0 && track.cuts.length) {
    // Through the bushes on a shortcut: slower, if it's a shortcut worth a boost.
    const road = track.samples[kart.idx];
    const off = Math.abs((kart.x - road.x) * road.nx + (kart.z - road.z) * road.nz) > road.half;
    const near = off ? nearestCut(track, kart.x, kart.z) : null;
    if (near && near.dist < near.half) top *= near.cut.speed;
  }
  // Slower up a hill, quicker down one.
  const here = track.samples[kart.idx];
  const rise = (track.samples[(kart.idx + 1) % track.n].y - track.samples[(kart.idx - 1 + track.n) % track.n].y) / 2;
  const climb = rise * (Math.cos(kart.heading) * here.tx + Math.sin(kart.heading) * here.tz);
  top *= 1 - clamp(climb * HILL, -0.3, 0.3);

  if (kart.boost > 0) kart.speed = Math.max(kart.speed, top * 0.92);
  kart.speed += clamp(top - kart.speed, -ACCEL * 2.5 * STEP, ACCEL * STEP);
  // In the air, the wheels have little to turn against; on ice, not much more.
  const icy = kart.y <= 0 && (kart.slide > 0 || onIce(track, kart.idx));
  const grip = kart.y > 0 ? 0.35 : icy ? ICE_GRIP : 1;
  // Your own drift slides: the kart swings into the corner but travels wide of where it points.
  const sliding = !icy && manual && kart.drift !== 0 && kart.y <= 0;
  if (kart.spin <= 0) {
    // In a drift, always into it: the steering only says how tightly, and it tightens as the drift settles in.
    const D = TUNING.drift;
    const into = clamp((steer * kart.drift - D.releaseSteer) / (1 - D.releaseSteer), 0, 1);
    const settled = D.rampFrom + (1 - D.rampFrom) * Math.min(1, kart.charge / D.ramp);
    const lock = kart.drift && manual ? kart.drift * (D.turnMin + (D.turnMax - D.turnMin) * into) * settled : kart.steer;
    const turn = lock * TURN * STEP * Math.min(1, kart.speed / 8) * grip;
    if (icy || sliding) {
      // The kart turns, but keeps sliding the way it was going — all of it on ice, a share in a drift.
      const carry = icy ? 1 : D.slide;
      kart.kx += carry * kart.speed * (Math.cos(kart.heading) - Math.cos(kart.heading + turn));
      kart.kz += carry * kart.speed * (Math.sin(kart.heading) - Math.sin(kart.heading + turn));
    }
    kart.heading += turn;
  }

  const ground = roadY(track, kart.idx, kart.x, kart.z);
  kart.x += (Math.cos(kart.heading) * kart.speed + kart.kx) * STEP;
  kart.z += (Math.sin(kart.heading) * kart.speed + kart.kz) * STEP;
  const decay = icy ? ICE_DECAY : sliding ? DRIFT_DECAY : KNOCK_DECAY;
  kart.kx *= decay;
  kart.kz *= decay;
  settle(race, kart, true);
  // In the air, height is kept from the ground under you, which moves as the road rises and falls.
  if (kart.y > 0 || kart.vy > 0) kart.y -= roadY(track, kart.idx, kart.x, kart.z) - ground;
  hazards(race, kart);

  const wasOnPad = kart.onPad;
  kart.onPad = false;
  for (const pad of track.pads) {
    const s = track.samples[kart.idx];
    const lat = (kart.x - s.x) * s.nx + (kart.z - s.z) * s.nz;
    const along = loopDelta(pad.idx, kart.idx, track.n);
    if (Math.abs(along) < PAD_LENGTH / 2 && Math.abs(lat - pad.lat) < PAD_WIDTH / 2) {
      giveBoost(kart, TUNING.boost.pad);
      if (!wasOnPad) emit(race, { kind: "pad", kart: kart.id });
      kart.onPad = true;
    }
    // A croquet hoop over the pad: clip a post and you're pushed off it,
    // slowed for how square on you hit it — so you slide round it, not stick.
    if (!track.map.wickets || Math.abs(along) > 2 || mighty(kart)) continue;
    const p = track.samples[pad.idx];
    for (const side of SIDES) {
      const post = pad.lat + side * (PAD_WIDTH / 2 + POST_RADIUS);
      const dx = kart.x - (p.x + p.nx * post);
      const dz = kart.z - (p.z + p.nz * post);
      const dist = Math.hypot(dx, dz);
      if (dist >= KART_RADIUS + POST_RADIUS || dist === 0) continue;
      const nx = dx / dist;
      const nz = dz / dist;
      kart.x += nx * (KART_RADIUS + POST_RADIUS - dist);
      kart.z += nz * (KART_RADIUS + POST_RADIUS - dist);
      kart.speed *= 1 - 0.3 * Math.max(0, -(Math.cos(kart.heading) * nx + Math.sin(kart.heading) * nz));
    }
  }

  const ramp = rampUnder(race, kart);
  const { window } = TUNING.trick;
  if (ramp !== null && kart.y <= ramp + 0.3) {
    // Up a snow ramp, rising as fast as it does — and off the lip, faster
    // still. The time for a trick starts running at the lip.
    kart.y = ramp;
    kart.vy = kart.speed * (RAMP_HEIGHT / RAMP_LENGTH) * RAMP_KICK;
    kart.trick = window;
  } else if (kart.y > 0 || kart.vy > 0) {
    kart.vy -= GRAVITY * STEP;
    kart.y += kart.vy * STEP;
    kart.trick = Math.max(0, kart.trick - STEP);
    if (kart.tricked > 0) kart.tricked += STEP;
    else if (!manual && !kart.tricked && kart.trick < window - 0.1) {
      // A computer driver goes for a trick, or doesn't, a moment off the lip.
      kart.tricked = race.rng() < diff.trick ? STEP : -1;
      if (kart.tricked > 0) emit(race, { kind: "trick", kart: kart.id });
    }
    if (kart.y <= 0) {
      // Down again, and a kick for landing it — a big one for landing a trick.
      kart.y = kart.vy = 0;
      if (kart.spin <= 0) giveBoost(kart, kart.tricked > 0 ? TUNING.trick.boost : TUNING.boost.landing);
      kart.trick = kart.tricked = 0;
      emit(race, { kind: "land", kart: kart.id });
    }
  } else if (kart.speed > 4) {
    for (const branch of track.branches) {
      const s = track.samples[kart.idx];
      const lat = (kart.x - s.x) * s.nx + (kart.z - s.z) * s.nz;
      if (Math.abs(loopDelta(branch.idx, kart.idx, track.n)) < 1 && Math.abs(lat - branch.lat) < BRANCH_WIDTH / 2) {
        kart.vy = JUMP;
        kart.trick = window;
      }
    }
  }
  // The player's flick, in time.
  if (manual && input.trick && kart.trick > 0 && !kart.tricked && kart.spin <= 0) {
    kart.tricked = STEP;
    emit(race, { kind: "trick", kart: kart.id });
  }
}

/** How high the snow ramp under a kart is there, or null if it isn't on one. */
function rampUnder(race: Race, kart: Kart) {
  const { track } = race;
  const s = track.samples[kart.idx];
  const lat = (kart.x - s.x) * s.nx + (kart.z - s.z) * s.nz;
  const off = (kart.x - s.x) * s.tx + (kart.z - s.z) * s.tz;
  for (const ramp of track.ramps) {
    const along = loopDelta(ramp.idx - RAMP_LENGTH, kart.idx, track.n) + off;
    if (along >= 0 && along < RAMP_LENGTH && Math.abs(lat - ramp.lat) < RAMP_WIDTH / 2) return (RAMP_HEIGHT * along) / RAMP_LENGTH;
  }
  return null;
}

function moveShot(race: Race, shot: Shot) {
  const { track } = race;
  shot.ttl -= STEP;
  shot.grace -= STEP;
  if (isPuddle(shot)) return;

  // A rocket's after whoever's leading now.
  if (shot.kind === "rocket") shot.target = race.karts.find((k) => k.place === 1)?.id ?? shot.target;
  if ((shot.kind === "mosquito" || shot.kind === "rocket") && shot.target !== null) {
    const target = race.karts.find((k) => k.id === shot.target)!;
    let aimX = target.x;
    let aimZ = target.z;
    // Far off, follow the road round the corners; close in, go straight at it.
    if (Math.hypot(target.x - shot.x, target.z - shot.z) > 16) {
      const s = track.samples[(shot.idx + 10) % track.n];
      aimX = s.x;
      aimZ = s.z;
    }
    const speed = Math.hypot(shot.vx, shot.vz);
    const cur = Math.atan2(shot.vz, shot.vx);
    const rate = shot.kind === "rocket" ? 9 : 6;
    const turn = clamp(wrapAngle(Math.atan2(aimZ - shot.z, aimX - shot.x) - cur), -rate * STEP, rate * STEP);
    shot.vx = Math.cos(cur + turn) * speed;
    shot.vz = Math.sin(cur + turn) * speed;
  }

  shot.x += shot.vx * STEP;
  shot.z += shot.vz * STEP;
  const wall = confine(track, shot, 0.5);
  if (wall) {
    const dot = shot.vx * wall.nx + shot.vz * wall.nz;
    if (dot < 0) {
      // A glove or a can bounces off the wall; the swarm just follows it round.
      const k = shot.kind === "glove" || shot.kind === "can" ? 2 : 1;
      shot.vx -= k * dot * wall.nx;
      shot.vz -= k * dot * wall.nz;
    }
  }
}

// Scratch space for step(), reused so a step allocates nothing.
const bumped = new Set<Kart>();
const spent = new Set<Shot>();
const ranking: Kart[] = [];
let ranked: Race | null = null;
const byPlace = (a: Kart, b: Kart) => {
  if (a.finishTime !== null || b.finishTime !== null) {
    return (a.finishTime ?? Infinity) - (b.finishTime ?? Infinity);
  }
  return progress(ranked!, b) - progress(ranked!, a);
};

export function step(race: Race, input: Input) {
  race.time += STEP;
  for (const k of race.karts) {
    k.px = k.x;
    k.py = k.y;
    k.pz = k.z;
    k.ph = k.heading;
    k.ps = k.steer;
  }
  for (const s of race.shots) {
    s.px = s.x;
    s.pz = s.z;
  }
  const player = race.karts.find((k) => k.player);
  if (race.phase === "countdown") {
    if (input.held && race.startPress === null) race.startPress = race.time;
    if (race.time >= COUNTDOWN) {
      race.phase = "racing";
      if (player && !race.autopilot && race.startPress !== null) {
        // Touched down as the last number showed: away with a boost. Before
        // that: flooded, and stalled on the line a moment.
        const good = COUNTDOWN - race.startPress <= TUNING.start.early;
        if (good) giveBoost(player, TUNING.boost.start);
        else player.stall = TUNING.start.stall;
        emit(race, { kind: "start", kart: player.id, good });
      }
    }
    return;
  }

  const playerProgress = player ? progress(race, player) : 0;
  // The last stretch of the last lap: the rubber band at half strength.
  const fade = player && playerProgress > (race.laps - TUNING.rubber.finalFade) * race.track.n ? 0.5 : 1;
  for (const kart of race.karts) {
    driveKart(race, kart, input, playerProgress - progress(race, kart), fade);
  }
  if ((input.fire || input.back) && player && !race.autopilot && player.finishTime === null) fireItem(race, player, !input.fire);
  // A speaker stack by the road throws karts near it away from it, on every beat.
  if (Math.floor(race.time / BASS_BEAT) > Math.floor((race.time - STEP) / BASS_BEAT)) {
    for (const h of race.track.hazards) {
      if (h.kind !== "speaker") continue;
      const s = race.track.samples[h.idx];
      bassPush(race, s.x + s.nx * h.lat, s.z + s.nz * h.lat, null);
    }
  }

  // Karts bump each other; a star knocks the other one spinning.
  const karts = race.karts;
  bumped.clear();
  for (let i = 0; i < karts.length; i++) {
    for (let j = i + 1; j < karts.length; j++) {
      if (bump(race, karts[i], karts[j])) {
        bumped.add(karts[i]);
        bumped.add(karts[j]);
      }
    }
  }
  // A shove mustn't leave anyone inside a wall.
  bumped.forEach((k) => settle(race, k, false));

  const diff = TUNING.difficulty[race.difficulty];
  for (const box of race.boxes) {
    if (box.respawn > 0) {
      box.respawn -= STEP;
      continue;
    }
    for (const kart of karts) {
      if (Math.hypot(kart.x - box.x, kart.z - box.z) > 2) continue;
      box.respawn = TUNING.items.boxRespawn;
      if (!kart.item) {
        kart.item = rollItem(race, kart);
        kart.queue.length = 0;
        // Zobbe's box: three more, all different.
        for (let tries = 0; kart.queue.length < boxSize(kart) - 1 && tries < 100; tries++) {
          const next = rollItem(race, kart);
          if (next !== kart.item && !kart.queue.includes(next)) kart.queue.push(next);
        }
        const [lo, hi] = diff.itemDelay;
        kart.itemTimer = (lo + race.rng() * (hi - lo)) * (kart.id === race.rival ? TUNING.rival.itemDelay : 1) * personality(kart.id).itemDelay;
        kart.roll = TUNING.items.rollTime;
        const back = backness(race, kart);
        emit(race, { kind: "box", kart: kart.id, item: kart.item, back, ...jackpot(race, kart, back) });
      }
      break;
    }
  }

  // Too close to a duck and it's off in a flap — and you've braked hard for it.
  for (const duck of race.ducks) {
    if (duck.gone > 0) {
      duck.gone -= STEP;
      continue;
    }
    const at = duckAt(race, duck);
    if (Math.abs(at.lat) > halfAt(race.track, duck.idx)) continue;
    for (const kart of karts) {
      if (kart.y > 0.4 || Math.hypot(kart.x - at.x, kart.z - at.z) > KART_RADIUS + 0.5) continue;
      duck.gone = DUCK_GONE;
      knock(race, kart, duck.skier ? "skier" : "duck");
      break;
    }
  }

  // Into a snowman or a kubb: it's knocked flat, and you thump to a crawl — unless you jumped it.
  for (const man of race.snowmen) {
    if (man.gone > 0) {
      man.gone -= STEP;
      continue;
    }
    for (const kart of karts) {
      if (kart.y > 1.4 || Math.hypot(kart.x - man.x, kart.z - man.z) > KART_RADIUS + man.r) continue;
      man.gone = SNOWMAN_GONE;
      knock(race, kart, man.kubb ? "kubb" : "snowman");
      break;
    }
  }

  for (const shot of race.shots) moveShot(race, shot);
  spent.clear();
  for (const shot of race.shots) {
    if (shot.ttl <= 0) spent.add(shot);
    for (const kart of karts) {
      if (spent.has(shot)) break;
      if (kart.id === shot.owner && shot.grace > 0) continue;
      // A rocket flies over everyone but the one it's after.
      if (shot.kind === "rocket" && kart.id !== shot.target) continue;
      if (Math.hypot(kart.x - shot.x, kart.z - shot.z) > KART_RADIUS + (isPuddle(shot) ? PUDDLE_REACH : 0.7)) continue;
      spent.add(shot);
      if (shot.kind === "rocket") {
        // It bursts: everyone near twirls.
        for (const k of karts) {
          if (Math.hypot(k.x - shot.x, k.z - shot.z) < TUNING.rocket.radius) hit(race, k, "rocket", shot.owner);
        }
        continue;
      }
      // A moose, a trolley, or just back from a twirl: it's spent on you, harmlessly.
      if (immune(kart) || kart.safe > 0) continue;
      if (shields(kart, shot)) {
        // Stopped by the beer, sick or sauce trailing behind — and that's used up.
        kart.item = kart.queue.shift() ?? null;
        emit(race, { kind: "block", kart: kart.id });
        continue;
      }
      if (shot.kind === "mosquito" || shot.kind === "barf" || shot.kind === "bearnaise") {
        emit(race, { kind: "hit", kart: kart.id, what: shot.kind, by: shot.owner, spin: false });
      }
      if (shot.kind === "mosquito") {
        kart.itch = ITCH_TIME;
        continue;
      }
      if (shot.kind === "barf") {
        kart.speed *= BARF_THUMP;
        kart.boost = 0;
        kart.soak = BARF_SOAK;
        continue;
      }
      if (shot.kind === "bearnaise") {
        // No grip, and a shove towards whichever wall is nearer.
        const s = race.track.samples[kart.idx];
        const side = Math.sign((kart.x - s.x) * s.nx + (kart.z - s.z) * s.nz) || 1;
        kart.slide = GLIDE_TIME;
        kart.boost = 0;
        kart.kx += s.nx * side * GLIDE;
        kart.kz += s.nz * side * GLIDE;
        continue;
      }
      hit(race, kart, shot.kind, shot.owner);
      if (shot.kind === "glove") {
        const v = Math.hypot(shot.vx, shot.vz) || 1;
        kart.kx += (shot.vx / v) * PUNCH;
        kart.kz += (shot.vz / v) * PUNCH;
      }
    }
  }
  // A glove or a swarm into anything else on the road takes both out.
  for (let i = 0; i < race.shots.length; i++) {
    for (let j = i + 1; j < race.shots.length; j++) {
      const a = race.shots[i];
      const b = race.shots[j];
      if ((isPuddle(a) && isPuddle(b)) || a.kind === "rocket" || b.kind === "rocket") continue;
      if (a.kind === "can" && b.kind === "can" && a.owner === b.owner) continue;
      if (Math.hypot(a.x - b.x, a.z - b.z) < 1.3) {
        spent.add(a);
        spent.add(b);
      }
    }
  }
  if (spent.size) {
    let kept = 0;
    for (const s of race.shots) if (!spent.has(s)) race.shots[kept++] = s;
    race.shots.length = kept;
  }

  ranking.length = 0;
  for (const k of karts) ranking.push(k);
  ranked = race;
  ranking.sort(byPlace);
  if (player) {
    // Whoever was ahead of the player and is now behind them: overtaken.
    const now = ranking.indexOf(player) + 1;
    for (let i = now; i < ranking.length; i++) {
      if (ranking[i].place < player.place) emit(race, { kind: "pass", kart: player.id, passed: ranking[i].id });
    }
  }
  ranking.forEach((k, i) => (k.place = i + 1));
  // The combo runs out if nothing's done in time.
  if (race.score.left > 0 && (race.score.left -= STEP) <= 0) breakChain(race);

  if (player && player.finishTime !== null && !race.autopilot) race.phase = "done";
}
